"use client";

import {
	type PersonaFields,
	type PersonaSource,
	personaAskerType,
	personaDisplayLabel,
	personaDisplayName,
	personaIndividualLine,
} from "@repo/core/client";
import { PersonaContextPreview } from "@ui/components/persona-context-preview";
import { PersonaRoleIcon } from "@ui/components/persona-role-icon";
import { cn } from "@ui/lib";
import {
	ArchiveIcon,
	ArchiveRestoreIcon,
	ChevronRightIcon,
	PencilIcon,
} from "lucide-react";
import { useFormatter, useTranslations } from "next-intl";
import { type ReactNode, useId, useState } from "react";

/**
 * The persona shape the card needs. Structural, so a stored `PersonaDTO`, a
 * generated `PersonaDraft` and the free tool's drafts all fit without mapping.
 */
export interface PersonaCardPersona extends PersonaFields {
	id?: string;
	/**
	 * Display label: an individual's title ("Retired homeowner"), a
	 * professional's role-first label ("CFO, early-stage startup"). Derived
	 * from the fields when absent; pass the disambiguated label
	 * (`personaDisplayLabels`) in lists so twins read differently.
	 */
	label?: string;
	version?: number;
	/** Shows the "Edited · vN" marker (product variant). Usually `version > 1`. */
	edited?: boolean;
	/** ISO date or datetime of the last edit, shown with the edited marker. */
	updatedAt?: string | null;
	archivedAt?: string | null;
	source?: PersonaSource;
	/** Pre-rendered context; rendered from the fields when absent. */
	contextText?: string;
	/** Why this persona asks these questions (drafts). */
	rationale?: string | null;
	/** Added to a project today; the day-1 run fires on add, so answers arrive today. */
	pendingFirstRun?: boolean;
}

export interface PersonaCardProps {
	persona: PersonaCardPersona;
	/**
	 * "product": editable, shows version / archived markers and actions.
	 * "marketing": the free tool; read-only and compact by default.
	 */
	variant?: "product" | "marketing";
	compact?: boolean;
	onEdit?: () => void;
	onArchive?: () => void;
	onRestore?: () => void;
	/** Extra header controls (e.g. a draft-selection checkbox). */
	actions?: ReactNode;
	footer?: ReactNode;
	/** Show the collapsible context preview. Default true. */
	showPreview?: boolean;
	defaultPreviewOpen?: boolean;
	/** Defaults: "What we send" (product) / "What we send AI as this person" (marketing). */
	previewLabel?: string;
	/** Adds the combined persona + prompt example inside the preview. */
	exampleQuery?: string;
	/** Selected ring, for draft review. */
	selected?: boolean;
	className?: string;
}

function Pill({ children }: { children: ReactNode }) {
	return (
		<span className="inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
			{children}
		</span>
	);
}

function IconAction({
	label,
	onClick,
	children,
}: {
	label: string;
	onClick: () => void;
	children: ReactNode;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			aria-label={label}
			title={label}
			className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
		>
			{children}
		</button>
	);
}

function BlockLabel({ children }: { children: ReactNode }) {
	return (
		<p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
			{children}
		</p>
	);
}

function Chips({
	items,
	label,
}: {
	items: readonly string[];
	label: string;
}) {
	return (
		<ul className="flex flex-wrap gap-1" aria-label={label}>
			{items.map((item) => (
				<li
					key={item}
					className="rounded-md bg-muted px-1.5 py-0.5 text-[11px] font-medium text-foreground"
				>
					{item}
				</li>
			))}
		</ul>
	);
}

function ListBlock({
	label,
	items,
}: {
	label: string;
	items: readonly string[];
}) {
	if (items.length === 0) {
		return null;
	}
	return (
		<div className="space-y-1">
			<BlockLabel>{label}</BlockLabel>
			<ul className="space-y-0.5 text-sm text-foreground">
				{items.map((item) => (
					<li key={item} className="flex gap-1.5">
						<span aria-hidden className="text-muted-foreground">
							·
						</span>
						<span>{item}</span>
					</li>
				))}
			</ul>
		</div>
	);
}

/**
 * One persona, either asker type. Someone at an organization: role icon,
 * role group, company line, the tools they already use, priorities and
 * constraints. A person: who they are, region · segment, "About them", what
 * they already have, what matters most and constraints. Both end with a
 * collapsible preview of the exact context we send. Presentational
 * and props-driven, shared by the product (personas page, project personas,
 * generate dialog) and the free prompt generator. Each surface adds its own
 * controls through `onEdit` / `onArchive` / `onRestore`, `actions` and
 * `footer`. Strings come from `personas.card`.
 */
export function PersonaCard({
	persona,
	variant = "product",
	compact,
	onEdit,
	onArchive,
	onRestore,
	actions,
	footer,
	showPreview = true,
	defaultPreviewOpen = false,
	previewLabel,
	exampleQuery,
	selected = false,
	className,
}: PersonaCardProps) {
	const t = useTranslations("personas.card");
	const format = useFormatter();
	const isProduct = variant === "product";
	const isCompact = compact ?? !isProduct;
	const archived = isProduct && Boolean(persona.archivedAt);
	const [open, setOpen] = useState(defaultPreviewOpen);
	const previewId = useId();
	const listLimit = isCompact ? 2 : 3;

	// Role-first: the label comes from the fields; a name is optional and only
	// ever shown as a secondary line when the user added one.
	const label = persona.label ?? personaDisplayLabel(persona);
	const name = personaDisplayName(persona);
	const individual = personaAskerType(persona) === "individual";
	const individualLine = individual ? personaIndividualLine(persona) : "";
	const companyLine = [
		persona.companySize ? t(`companySizes.${persona.companySize}`) : null,
		persona.industry?.trim() || null,
		persona.region?.trim() || null,
	]
		.filter(Boolean)
		.join(" · ");
	const roleGroupLabel = t(`roleGroups.${persona.roleGroup ?? "other"}`);
	const decisionRoleLabel = persona.decisionRole
		? t(`decisionRoles.${persona.decisionRole}`)
		: null;
	const editedDate = persona.updatedAt
		? format.dateTime(new Date(persona.updatedAt), {
				month: "short",
				day: "numeric",
				timeZone: "UTC",
			})
		: null;

	return (
		<article
			className={cn(
				"flex min-w-0 flex-col gap-3 rounded-xl border bg-card p-4",
				archived && "border-dashed opacity-70",
				selected && "ring-2 ring-primary",
				className,
			)}
		>
			<header className="flex items-start gap-3">
				<PersonaRoleIcon
					roleGroup={persona.roleGroup}
					askerType={persona.askerType}
					household={persona.household}
				/>
				<div className="min-w-0 flex-1">
					<div className="flex flex-wrap items-center gap-1.5">
						<h3 className="min-w-0 font-semibold leading-tight">
							{label}
						</h3>
						{archived ? <Pill>{t("archived")}</Pill> : null}
						{isProduct && persona.edited && persona.version ? (
							<Pill>
								{editedDate
									? t("editedOn", {
											version: persona.version,
											date: editedDate,
										})
									: t("edited", { version: persona.version })}
							</Pill>
						) : null}
						{isProduct && persona.pendingFirstRun && !archived ? (
							<Pill>{t("firstAnswersToday")}</Pill>
						) : null}
					</div>
					{name ? (
						<p className="truncate text-sm text-muted-foreground">
							{name}
						</p>
					) : null}
					{individual && individualLine ? (
						<p className="mt-0.5 text-xs text-muted-foreground">
							{individualLine}
						</p>
					) : null}
				</div>
				{actions}
				{isProduct && (onEdit || onArchive || onRestore) ? (
					<div className="flex shrink-0 items-center">
						{onEdit && !archived ? (
							<IconAction
								label={t("editAction", { label })}
								onClick={onEdit}
							>
								<PencilIcon className="size-4" />
							</IconAction>
						) : null}
						{onArchive && !archived ? (
							<IconAction
								label={t("archiveAction", { label })}
								onClick={onArchive}
							>
								<ArchiveIcon className="size-4" />
							</IconAction>
						) : null}
						{onRestore && archived ? (
							<IconAction
								label={t("restoreAction", { label })}
								onClick={onRestore}
							>
								<ArchiveRestoreIcon className="size-4" />
							</IconAction>
						) : null}
					</div>
				) : null}
			</header>

			{individual ? (
				<>
					{persona.household?.trim() ? (
						<div className="space-y-1">
							<BlockLabel>{t("aboutThem")}</BlockLabel>
							<p className="text-sm text-foreground">
								{persona.household}
							</p>
						</div>
					) : null}
					{persona.techStack && persona.techStack.length > 0 ? (
						<div className="space-y-1">
							<BlockLabel>{t("alreadyHas")}</BlockLabel>
							<Chips
								items={persona.techStack}
								label={t("alreadyHas")}
							/>
						</div>
					) : null}
				</>
			) : (
				<>
					<div className="flex flex-wrap gap-1.5">
						<Pill>{roleGroupLabel}</Pill>
						{persona.segment ? (
							<Pill>{persona.segment}</Pill>
						) : null}
						{decisionRoleLabel &&
						!isCompact &&
						decisionRoleLabel !== roleGroupLabel ? (
							<Pill>{decisionRoleLabel}</Pill>
						) : null}
					</div>

					{companyLine ? (
						<p className="text-xs text-muted-foreground">
							{companyLine}
						</p>
					) : null}

					{persona.techStack && persona.techStack.length > 0 ? (
						<Chips
							items={persona.techStack}
							label={t("techStack")}
						/>
					) : null}
				</>
			)}

			<div className={cn("grid gap-3", !isCompact && "sm:grid-cols-2")}>
				<ListBlock
					label={individual ? t("whatMattersMost") : t("priorities")}
					items={(persona.priorities ?? []).slice(0, listLimit)}
				/>
				<ListBlock
					label={t("constraints")}
					items={(persona.constraints ?? []).slice(0, listLimit)}
				/>
			</div>

			{persona.rationale ? (
				<p className="text-xs text-muted-foreground">
					{persona.rationale}
				</p>
			) : null}

			{showPreview ? (
				<div className="border-t pt-3">
					<button
						type="button"
						aria-expanded={open}
						aria-controls={previewId}
						onClick={() => setOpen((v) => !v)}
						className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
					>
						<ChevronRightIcon
							className={cn(
								"size-3.5 transition-transform",
								open && "rotate-90",
							)}
						/>
						{previewLabel ??
							(isProduct ? t("whatWeSend") : t("whatWeSendAi"))}
					</button>
					{open ? (
						<div id={previewId} className="mt-2">
							<PersonaContextPreview
								fields={persona}
								context={persona.contextText}
								exampleQuery={exampleQuery}
								label={
									isProduct
										? t("whatWeSend")
										: t("asThisPerson")
								}
								showCount={isProduct}
								compact
							/>
						</div>
					) : null}
				</div>
			) : null}

			{footer}
		</article>
	);
}
